import { Box, Heading, Tooltip, Icon, HStack, Text } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterForm from "../../components/FilterForm";
import AgentServicesSessionTime from "../../components/branches/reports/AgentServicesSessionTime";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import Loader from "../../components/loader";
import {
  getReportDescription,
  getReportRangeString,
} from "../../store/helpers";
import _ from "lodash";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import useReportRenderSettingLoader from "../../hooks/useReportRenderSettingLoader";
import {
  DEFAULT_RENDER_CONFIG,
  getRenderConfigSummaryItems,
  normalizeRenderConfig,
} from "../../store/renderConfig";
import { saveReportRenderSetting } from "../../store/actions/defaultAction";
import { downloadAgentServiceTimeCsvFiles } from "../../store/agentServiceTimeExport";

const AgentServiceTimePerService = ({
  showFilterForm = true,

  showTitle = 1,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.authReducer);
  const { loading, reportRange, reportsData, branchId, organizationId, savingReportRenderSetting } = useSelector(
    (state) => state.defaultReducer
  );
  const [renderConfig, setRenderConfig] = useState(DEFAULT_RENDER_CONFIG);

  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_branch_agent_service_time_per_service
      : REPORT_TYPE_IDS.monthly_branch_agent_service_time_per_service;

  const data = useMemo(() => {
    let reports = reportsData.filter((a) => a.reportTypeId === reportTypeId);
    if (branchId.length > 0) {
      reports = reports.filter((a) => a.branchId === branchId);
    }
    return reports.map((a) => a.data) || [];
  }, [branchId, reportTypeId, reportsData]);

  const title = `Agent Service Time Per Service`;
  const isLoadingRenderSetting = useReportRenderSettingLoader({
    userId: userData?.id,
    organizationId,
    reportTypeId,
    setRenderConfig,
  });
  const activeRenderConfig = useMemo(() => normalizeRenderConfig(renderConfig), [renderConfig]);
  const presentationSummaryItems = useMemo(
    () => getRenderConfigSummaryItems(activeRenderConfig),
    [activeRenderConfig]
  );

  const saveRenderConfig = () => {
    dispatch(
      saveReportRenderSetting({
        userId: userData.id,
        organizationId,
        reportTypeId,
        renderConfig: activeRenderConfig,
      })
    );
  };

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>
            {getReportRangeString(reportRange)} Branch Agent Service Time Per
            Service
          </title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : <></>}
      {loading || isLoadingRenderSetting ? (
        <>
          <Loader />
        </>
      ) : (
        <>
          {" "}
          {!_.isEmpty(data) ? (
            <>
              <HStack flexDirection={"row"} justifyContent={"space-between"}>
                <Heading fontSize="34px" color="#222" fontWeight="700">
                  {title}

                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content>
                        {getReportDescription(reportRange).AgentServiceSessionTime}
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                </Heading>
                <DownloadReport
                  reportRange={reportRange}
                  reportTypeId={reportTypeId}
                  renderConfig={activeRenderConfig}
                  onSave={saveRenderConfig}
                  isSaving={savingReportRenderSetting}
                  onDownloadCsv={() =>
                    downloadAgentServiceTimeCsvFiles({
                      data,
                      renderConfig: activeRenderConfig,
                    })
                  }
                />
              </HStack>

              <ReportPresentationControls
                renderConfig={activeRenderConfig}
                onChange={setRenderConfig}
                supportsTableGrouping={true}
              />
              <ReportPresentationSummary items={presentationSummaryItems} />

              <AgentServicesSessionTime
                data={data}
                renderConfig={activeRenderConfig}
                onRenderConfigChange={setRenderConfig}
              />
            </>
          ) : (
        <SelectBranchInfo message={'Update Filters and Hit Search to retrieve data'}/>
      )}
        </>
      )}
    </Box>
  );
};

export default AgentServiceTimePerService;
